import { FormEventHandler, ReactNode } from "react";

import Box from "@mui/material/Box";
import Container, { ContainerProps } from "@mui/material/Container";
import MaterialModal, {
  ModalProps as MaterialModalProps,
} from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import { CloseButton } from "../buttons";

interface ModalProps {
  open: boolean;
  onClose(): void;
  onSubmit?: FormEventHandler<HTMLDivElement>;
  children: ReactNode;
  maxWidth?: ContainerProps["maxWidth"];
  title?: string | ReactNode;
  sx?: MaterialModalProps["sx"];
  modalSx?: MaterialModalProps["sx"];
  image?: ReactNode;
  disableClose?: boolean;
  hideCloseButtonAndTitle?: boolean;
}

export function Modal({
  open,
  onClose,
  onSubmit,
  children,
  title,
  maxWidth = "lg",
  sx = {},
  modalSx = {},
  image,
  disableClose,
  hideCloseButtonAndTitle = false,
}: ModalProps) {
  const theme = useTheme();
  return (
    <MaterialModal
      open={open}
      onClose={disableClose ? undefined : onClose}
      onSubmit={onSubmit}
      sx={{
        zIndex: 1200,
        ...modalSx,
        "&:focus": {
          outline: "none",
        },
        "&:focus-visible": {
          outline: "none",
        },
        "& .MuiBackdrop-root": {
          outline: "none",
        },
      }}>
      <Container
        maxWidth={maxWidth}
        sx={{
          height: "100%",
          display: "flex",
          padding: theme.spacing(3, 0),
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          outline: "none",
          border: "none",
          "&:focus": {
            outline: "none",
          },
          "&:focus-visible": {
            outline: "none",
          },
          ...sx,
        }}>
        <Paper
          sx={{
            maxHeight: "100%",
            display: "flex",
            flexDirection: "column",
            padding: 0,
            overflow: "hidden",
            width: "inherit",
          }}>
          {!hideCloseButtonAndTitle &&
            (!!image ? (
              <Box width="100%" px={3} pt={3} pb={2}>
                <Box display="flex" justifyContent="space-between">
                  {image}
                  <CloseButton onClick={onClose} disabled={disableClose} />
                </Box>
                <Typography variant="h3">{title}</Typography>
              </Box>
            ) : (
              <Box
                px={3}
                pt={3}
                pb={2}
                width={"100%"}
                display="flex"
                justifyContent="space-between">
                <Typography variant="h3">{title}</Typography>
                <CloseButton onClick={onClose} disabled={disableClose} />
              </Box>
            ))}
          <Box p={3} overflow="auto">
            {children}
          </Box>
        </Paper>
      </Container>
    </MaterialModal>
  );
}
